'use client'

import { useParams } from 'next/navigation'

import { useAppRoute } from '@lib/use-app-route'
import { useWorkosSummary } from '@resolvers/workos'

import { ENVIRONMENT_NAV, ORGANIZATION_NAV, TEAM_NAV, WORKOS_MOVE_NAV, type NavChild, type NavItem } from './nav'

export type SidebarChild = NavChild

export type SidebarItem = NavItem

export const isNavActive = (currentPath: string, href: string, exact?: boolean) => {
  if (exact) return currentPath === href
  return currentPath === href || currentPath.startsWith(`${href}/`)
}

const resolveChildren = (children: NavChild[], base: string) =>
  children.map((child) => ({ ...child, href: `${base}${child.href}` }))

const withWorkosMove = (items: NavItem[]) => {
  const position = items.findIndex((item) => item.href === '/organizations') + 1
  return [...items.slice(0, position), WORKOS_MOVE_NAV, ...items.slice(position)]
}

export const organizationPath = (envPath: (path?: string) => string, tenant: string, path = '') =>
  envPath(`/organizations/${encodeURIComponent(tenant)}${path}`)

export const useSidebarNav = () => {
  const { pathname, envPath, teamPath, scope } = useAppRoute()
  const params = useParams<{ tenant?: string }>()
  const tenant = typeof params?.tenant === 'string' ? decodeURIComponent(params.tenant) : undefined
  const { workos } = useWorkosSummary(scope)
  const environmentNav = workos ? withWorkosMove(ENVIRONMENT_NAV) : ENVIRONMENT_NAV

  const mainItems: SidebarItem[] = [
    ...environmentNav.map((item) => ({
      ...item,
      href: envPath(item.href),
      children: item.children && resolveChildren(item.children, envPath())
    })),
    ...TEAM_NAV.map((item) => ({
      ...item,
      href: teamPath(item.href),
      children: item.children && resolveChildren(item.children, teamPath())
    }))
  ]

  const organizationGroups = tenant
    ? ORGANIZATION_NAV.map((group) => resolveChildren(group, organizationPath(envPath, tenant)))
    : null

  return { mainItems, organizationGroups, tenant, currentPath: pathname }
}
